import React, { useEffect, useRef, useState } from 'react';
import { Panels } from '../Consts';
import {
  Checkbox, Button, FormItem, FormLayoutGroup, Group, Panel, Separator, PullToRefresh
} from '@vkontakte/vkui';
import { useAppContext } from '../../AppContext';
import { NameInputComponent } from './components/NameInputComponent';
import { CommentInputComponent } from './components/CommentInputComponent';
import { AmountInputComponent } from './components/AmountInputComponent';
import { MembersInputComponent } from './components/MembersInputComponent';
import { DateInputComponent } from './components/DateInputComponent';
import { UserSelectComponent } from './components/UserSelectComponent';
import { AddJoinedMembersComponent } from './components/AddJoinedMembersComponent';
import { clearCache as eventInfoCacheClear } from '../Events/ShowEventPanel';
import { ErrorLoadComponent } from '../components/ErrorLoadComponent';
import { getResponseFromError } from '../../helpers/helpers';
import { MAX_AMOUNT, MAX_PAYMENT_AMOUNT, MIN_AMOUNT } from '../../Consts';
import { getRouteEventInfo } from '../../route';
import { PlatformPanelHeader } from '../components/PlatformPanelHeader';
import PropTypes from 'prop-types';

const CURRENT_PANEL = Panels.CREATE_PAYMENTS_FROM_QR_CODE_PANEL;

const Item = ({item, setItem, members}) => {
  const context = useAppContext();

  return (<Group>
      <Checkbox
        description={context.lang().getKey(CURRENT_PANEL, 'skip_label')}
        checked={item.delete}
        onChange={(event) => {
          item.delete = event.target.checked;
          setItem({...item});
        }}
      ></Checkbox>
      <NameInputComponent
        error={item.nameError || ''}
        setError={(value) => {
          item.nameError = value;
          setItem({...item});
        }}
        name={item.name}
        setName={(value) => {
          item.name = value;
          setItem({...item});
        }}
        disabled={item.delete}
        label={context.lang().getKey('app', 'name_input_label')}
        errorTextMinLength={context.lang().getKey('errors', 'name_error_min_length_3_char')}
      />
      {
        !item.delete ? (<>
          <CommentInputComponent
            error={item.commentError || ''}
            setError={(value) => {
              item.commentError = value;
              setItem({...item});
            }}
            comment={item.comment}
            setComment={(value) => {
              item.comment = value;
              setItem({...item});
            }}
            label={context.lang().getKey('app', 'comment_input_label')}
            errorTextMaxLength={context.lang().getKey('errors', 'comment_error_max_length_255_char')}
          />
          <AmountInputComponent
            error={item.amountError || ''}
            setError={(value) => {
              item.amountError = value;
              setItem({...item});
            }}
            amount={item.amount}
            setAmount={(value) => {
              item.amount = value;
              setItem({...item});
            }}
            min={MIN_AMOUNT}
            max={MAX_PAYMENT_AMOUNT}
            label={context.lang().getKey('app', 'amount_input_label')}
            errorTextMin={context.lang().getKey('errors', 'amount_error_min')}
            errorTextMax={context.lang().getKey('errors', 'amount_error_max')}
          />
          <MembersInputComponent
            error={item.membersError || ''}
            setError={(value) => {
              item.membersError = value;
              setItem({...item});
            }}
            members={members}
            value={item.users}
            setValue={(value) => {
              item.users = value;
              setItem({...item});
            }}
            label={context.lang().getKey('app', 'members_input_label')}
            placeholder={context.lang().getKey('app', 'members_input_placeholder')}
            allItemsLabel={context.lang().getKey('app', 'all_members_items_label')}
          />
        </>) : undefined
      }
  </Group>);
}

const SelectItemType = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  img: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
})

const ItemType = PropTypes.shape({
  users: PropTypes.arrayOf(SelectItemType.isRequired).isRequired,
  comment: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  delete: PropTypes.bool.isRequired,
})

Item.protoType = {
  item: PropTypes.arrayOf(ItemType.isRequired).isRequired,
  setItem: PropTypes.func.isRequired,
  members: PropTypes.arrayOf().isRequired,
}

const CreateManyPaymentsFromQrCodePanel = () => {
  const context = useAppContext();
  const params = context.router.getParams();

  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [date, setDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [creator, setCreator] = useState(0);
  const [creatorError, setCreatorError] = useState('');
  const [sendSave, setSendSave] = useState(false);
  const [addJoinedMembers, setAddJoinedMembers] = useState(true);

  const isFormSavedRef = useRef(false);
  const [errorInit, setErrorInit] = useState(null);

  const fetch = (spinner = true) => {
    if (spinner) {
      context.spinner(true);
    }

    context.init().then(() => {
      context.httpClient()
        .getEventV3(params.eventId)
        .then((response) => {
          const usersForSelect = response.data.users.map((user) => ({
            value: user.id,
            label: user.name,
            img: user.img,
            type: user.type,
          }));
          setMembers(usersForSelect);

          const owner = usersForSelect.filter((user) => user.value === context.store.currentUserId);
          const items = (params.items || []).map((item) => {
            const name = item.name || '';
            const amount = item.amount || item.price || 0.0;
            const quantity = item.quantity || 1.0;
            const total_amount = item.total_amount || (amount * quantity);

            return {
              users: owner,
              comment: context.lang().formatPrice(amount) + ' x ' + quantity,
              name: name,
              amount: total_amount.toFixed(2),
              delete: false,
            };
          });
          setItems([...items]);

          setErrorInit(null);
          context.spinner(false);
        })
        .catch((error) => {
          context.spinner(false);
          setErrorInit(error);
        })
    })
    .catch((error) => {
      context.spinner(false);
      setErrorInit(error);
    });
  }

  useEffect(() => {
    setCreator(context.store.currentUserId);
    setDate(context.lang().dateFormatToInput(new Date(params.date)));
    fetch();
  }, []);

  const onSave = () => {
    if (isFormSavedRef.current) {
      return;
    }

    isFormSavedRef.current = true;
    setSendSave(true);
    context.spinner(true);
    context.httpClient()
      .addEventItemsManyV4(params.eventId, items.filter((item) => !item.delete), date, creator, addJoinedMembers, (params.receiptId || ''))
      .then(() => {
        isFormSavedRef.current = false;
        setSendSave(false);
        context.spinner(false);
        eventInfoCacheClear(params.eventId, context.cache);
        context.router.backTo(Panels.EVENT_INFO)
          .then(() => {})
          .catch((r) => r.replace(...getRouteEventInfo(params.eventId)))
      })
      .catch((error) => {
        eventInfoCacheClear(params.eventId, context.cache);
        isFormSavedRef.current = false;
        setSendSave(false);
        context.spinner(false);
        const response = getResponseFromError(error);
        if (!response) {
          context.showError(error);
          return;
        }

        setDateError(response.date || '');
        delete response['date'];
        setCreatorError(response.creator || '');
        delete response['creator'];

        let index = 0;
        items.forEach((item, i) => {
          if (item.delete) {
            items[i].nameError = '';
            items[i].commentError = '';
            items[i].amountError = '';
            items[i].membersError = '';
          } else {
            items[i].nameError = (response['items.' + index + '.name'] || '');delete response['items.' + index + '.name'];
            items[i].commentError = (response['items.' + index + '.comment'] || '');delete response['items.' + index + '.comment'];
            items[i].amountError = (response['items.' + index + '.amount'] || '');delete response['items.' + index + '.amount'];
            items[i].membersError = (response['items.' + index + '.users'] || '');delete response['items.' + index + '.users'];
            index++;
          }
        });
        setItems([...items]);

        if (response.message) {
          context.showError(null, response.message);
        } else if (Object.keys(response).length > 0) {
          context.showError(error);
        }
      });

    context.getBridge().retargeting.createPayment(context.store.currentUserId);
  }

  if (errorInit) {
    return (<ErrorLoadComponent
      error={errorInit}
      onFetch={fetch}
    />)
  }

  return (<Panel id={CURRENT_PANEL}>
    <PlatformPanelHeader
      text={context.lang().getKey(CURRENT_PANEL, 'header')}
    />
    {context.snackbar}
    <PullToRefresh onRefresh={() => fetch(false)}>
      {
        items.map((item, number) => (<Item
          key={number}
          item={item}
          setItem={(newItem) => {
            items[number] = newItem;
            setItems([...items]);
          }}
          users={item.users}
          members={members}
        />))
      }
      <Separator />
      <form>
        <FormLayoutGroup mode="vertical">
          <Group>
            <DateInputComponent
              defaultDate={context.lang().dateFormatToInput(new Date())}
              dateTime={date}
              setDateTime={setDate}
              error={dateError}
              setError={setDateError}
              label={context.lang().getKey('app', 'date_input_label')}
              errorTextMinValue={context.lang().getKey('errors', 'date_error_min')}
              errorTextMaxValue={context.lang().getKey('errors', 'date_error_max')}
            />
            <UserSelectComponent
              label={context.lang().getKey('app', 'creator_input_label')}
              placeholder={context.lang().getKey('app', 'creator_input_placeholder')}
              error={creatorError}
              setError={setCreatorError}
              members={members}
              value={creator}
              setValue={setCreator}
            />
            <AddJoinedMembersComponent
              label={context.lang().getKey('app', 'add_joined_members_input_label')}
              checked={addJoinedMembers}
              onChange={(event) => setAddJoinedMembers(event.target.checked)}
              disabled={false}
            />
          </Group>
          <FormItem>
            <Button
              size="l"
              stretched={true}
              onClick={() => onSave()}
              loading={sendSave}
              disabled={sendSave || items.filter((item) => !item.delete).length < 1}
            >
              {context.lang().getKey(CURRENT_PANEL, 'button_save_label')}
            </Button>
          </FormItem>
        </FormLayoutGroup>
      </form>
    </PullToRefresh>
  </Panel>);
}

export default CreateManyPaymentsFromQrCodePanel;